import { z } from 'zod';

/**
 * §15 Support — the request shapes for the two INTAKE surfaces.
 *
 * A ticket has three possible origins and, until now, only one of them could
 * actually raise one:
 *
 *   · an AFFILIATE, from its portal. Built — see `affiliate.ts` for its
 *     topics and `modules/affiliate/portal-office.ts` for the routes.
 *   · an AGENT, from its portal. The schema carried `partner_kind = 'AGENT'`
 *     and the back-office queue already joined `agent.agent` to name it, but
 *     no route existed, so the portal's "New ticket" was a disabled button.
 *   · a PLAYER. There is still no player-facing intake; what exists now is the
 *     operator LOGGING one on a player's behalf, which is the console's own
 *     "New ticket" dialog and the shape at the foot of this file.
 *
 * Its own file rather than more of `lists.ts`: that file describes the support
 * QUEUE — what an operator filters, reassigns and closes — and these are the
 * shapes that put a ticket into it in the first place. They are also read by
 * three different realms (agent portal, back office, and the API in between),
 * which is exactly when a shared contract earns its place.
 */

/* ── The agent portal's intake ──────────────────────────────────────────── */

/**
 * Topics an agent can raise a ticket under.
 *
 * `support.ticket.topic` is a free-text varchar with no enum behind it, so this
 * list IS the enum: the back office's support queue groups by topic, and a
 * partner typing its own would make every group a group of one.
 *
 * Deliberately NOT the same list as `affiliateTicketTopic`. Five of the six
 * coincide, but an agent has a downline and an affiliate has traffic — "Sub-agent
 * issue" and "Traffic or link issue" are the questions each domain actually
 * asks, and one shared list would offer each partner a topic it cannot mean.
 */
export const agentTicketTopic = z.enum([
  'Payout question',
  'Sub-agent issue',
  'Player complaint',
  'Request a game',
  'Request a promotion',
  'Something else',
]);
export type AgentTicketTopic = z.infer<typeof agentTicketTopic>;

export const createAgentTicketRequest = z.object({
  topic: agentTicketTopic,
  subject: z.string().trim().min(3, 'Give the ticket a subject.').max(300),
  body: z.string().trim().min(3, 'Say what the platform team needs to know.').max(4000),
});
export type CreateAgentTicketRequest = z.infer<typeof createAgentTicketRequest>;

export const agentTicketReplyRequest = z.object({
  body: z.string().trim().min(1, 'Write a reply.').max(4000),
});
export type AgentTicketReplyRequest = z.infer<typeof agentTicketReplyRequest>;

/* ── The console's intake, on a player's behalf ─────────────────────────── */

/**
 * How the player reached the desk.
 *
 * CODES, not the words on the button: the column is new, and "In-app chat"
 * stored as prose is a column no report can group on twice running. The labels
 * live beside the codes in `SUPPORT_TICKET_CHANNELS` below so the console and
 * the database cannot disagree about the set.
 *
 * The same four values are written as a CHECK constraint in
 * `packages/db/sql/220_support_channel.sql`. Two statements of one closed set,
 * on purpose — the constraint is what holds when a route forgets, and the file
 * says so. Adding a fifth channel means editing both, and the SQL header says
 * that too.
 */
export const supportTicketChannel = z.enum(['IN_APP', 'EMAIL', 'MESSENGER', 'PHONE']);
export type SupportTicketChannel = z.infer<typeof supportTicketChannel>;

/** The channel codes with the words an operator reads. One definition, two readers. */
export const SUPPORT_TICKET_CHANNELS: readonly {
  key: SupportTicketChannel;
  label: string;
}[] = [
  { key: 'IN_APP', label: 'In-app chat' },
  { key: 'EMAIL', label: 'Email' },
  { key: 'MESSENGER', label: 'Messenger' },
  { key: 'PHONE', label: 'Phone' },
];

/** `support.ticket.priority`, which the queue orders on. */
export const supportTicketPriority = z.enum(['LOW', 'NORMAL', 'HIGH']);
export type SupportTicketPriority = z.infer<typeof supportTicketPriority>;

/**
 * An operator logging a ticket a player raised somewhere the platform cannot
 * see — a phone call, a Messenger thread, a walk-in.
 *
 * `playerId` is the ONLY identity on this request. There is no brandId and
 * there is no partner id: the ticket's brand is read from the player's own row
 * inside the operator's brand scope, so a request cannot file a ticket into a
 * brand the operator cannot see, and a partner ticket cannot be forged from
 * this surface at all.
 *
 * `body` is required, and it is the player's account of the problem as the
 * operator took it down. A ticket with a subject and no first message would
 * reach the queue with nothing for the next agent to read.
 */
export const createPlayerTicketRequest = z.object({
  playerId: z.string().min(1, 'Choose the player this is about.'),
  subject: z.string().trim().min(3, 'Give the ticket a subject.').max(300),
  body: z.string().trim().min(3, 'Write down what the player reported.').max(4000),
  channel: supportTicketChannel,
  priority: supportTicketPriority.default('NORMAL'),
});
export type CreatePlayerTicketRequest = z.infer<typeof createPlayerTicketRequest>;
